import PreviewOrderView from "@saleor/apps/components/PreviewOrder";
import { useUser } from "@saleor/auth";
import { useUserPermissions } from "@saleor/auth/hooks/useUserPermissions";
import { isStaffRole } from "@saleor/auth/hooks/useUserRole";
import { Zone } from "@saleor/auth/types";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { orderCustomerProcessingFailed } from "@saleor/createOrderCustomer/urls";
import ContractListCustomer from "@saleor/createOrderCustomer/views/ContractList";
import CustomerCreateOrder from "@saleor/createOrderCustomer/views/CreateOrderView";
import {
  DomesticOrderQueryParams,
  DomesticOrderSortField
} from "@saleor/createOrderDomestic/urls";
import CreateOrderDomestic from "@saleor/createOrderDomestic/views/CreateOrderView";
import {
  ExporPiListSortField,
  ExporPiListUrlQueryParams,
  ExportOrderQueryParams,
  ExportOrderSortField,
  ExportPiQueryParams,
  ExportPiSortField,
  ShoppingCartQueryParams,
  ShoppingCartSortField
} from "@saleor/createOrderExport/urls";
import CartDetailExport from "@saleor/createOrderExport/views/CartDetailExport";
import ContractDetailExport from "@saleor/createOrderExport/views/ContractDetailView";
import CreateOrderExport from "@saleor/createOrderExport/views/CreateOrderView";
import ShoppingCartExport from "@saleor/createOrderExport/views/ShoppingCartExport";
import ExportCreateOrder from "@saleor/createOrderExport/views/SoldTo";
import { PermissionEnum } from "@saleor/graphql";
import { sectionNames } from "@saleor/intl";
import { asSortParams } from "@saleor/utils/sort";
import { parse as parseQs } from "qs";
import React from "react";
import { useIntl } from "react-intl";
import { Route, RouteComponentProps, Switch } from "react-router";

import {
  ContracDetailQueryParams,
  ContracDetailSortField,
  contractDetailPath,
  ContractListUrlSortField,
  contractPath,
  createOrderUrl,
  CreateOrderUrlQueryParams,
  orderCartPath,
  OrderDetailQueryParams,
  OrderDetailSortField,
  orderPath,
  orderProcessing,
  previewOrderPath,
  shoppingCart
} from "./urls";
import Cart from "./views/Cart";
import CartDetail from "./views/CartDetail";
import ContractDetail from "./views/ContractDetail";
import ContractList from "./views/ContractList";
import CreateOrder from "./views/CreateOrderView";
import OrderProcessing from "./views/OrderProcessing";
import OrderProcessingFailedView from "./views/OrderProcessingFailed";
import SoldTo from "./views/SoldTo";

const SoldToList: React.FC<RouteComponentProps<any>> = ({ location }) => {
  const qs = parseQs(location.search.substr(1));
  return <SoldTo params={qs} />;
};
const ExportSoldToList: React.FC<RouteComponentProps<any>> = ({ location }) => {
  const qs = parseQs(location.search.substr(1));
  const params: ExporPiListUrlQueryParams = asSortParams(
    qs,
    ExporPiListSortField,
    ExporPiListSortField.piNo
  );
  return <ExportCreateOrder params={params} />;
};
const ContractListView: React.FC<RouteComponentProps<any>> = ({
  location,
  match
}) => {
  const qs = parseQs(location.search.substr(1));
  const params: CreateOrderUrlQueryParams = asSortParams(
    qs,
    ContractListUrlSortField,
    ContractListUrlSortField.contractNo
  );
  if (isStaffRole()) {
    // Staff
    return (
      <ContractList id={decodeURIComponent(match.params.id)} params={params} />
    );
  }
  return (
    <ContractListCustomer
      id={match?.params?.id ? decodeURIComponent(match.params.id) : ""}
      params={params}
    />
  );
};
const ContracDetailtListView: React.FC<RouteComponentProps<any>> = ({
  match
}) => {
  const qs = parseQs(location.search.substr(1));
  const permissions = useUserPermissions();
  const { zone } = useUser();
  const extensionsPermissionExport = permissions?.find(
    perm => perm.code === PermissionEnum.VIEW_ORDER_EXPORT
  );
  const extensionsPermissionDomestic = permissions?.find(
    perm => perm.code === PermissionEnum.VIEW_ORDER_DOMESTIC
  );

  if (
    (extensionsPermissionExport && !extensionsPermissionDomestic) ||
    zone === Zone.Export
  ) {
    const params: ExportPiQueryParams = asSortParams(
      qs,
      ExportPiSortField,
      ExportPiSortField.remain_quantity
    );
    return (
      <ContractDetailExport
        id={decodeURIComponent(match.params.id)}
        params={params}
      />
    );
  } else {
    const params: ContracDetailQueryParams = asSortParams(
      qs,
      ContracDetailSortField,
      ContracDetailSortField.total
    );
    return (
      <ContractDetail
        id={decodeURIComponent(match.params.id)}
        params={params}
      />
    );
  }
};

const CartDetailtListView: React.FC<RouteComponentProps<any>> = ({ match }) => {
  const qs = parseQs(location.search.substr(1));
  const params: OrderDetailQueryParams = asSortParams(
    qs,
    OrderDetailSortField,
    OrderDetailSortField.remain
  );
  return (
    <CartDetail id={decodeURIComponent(match.params.id)} params={params} />
  );
};

const CartDetailtExportListView: React.FC<RouteComponentProps<any>> = ({
  match
}) => <CartDetailExport checkOutId={decodeURIComponent(match.params.id)} />;

const ShoppingCartExportView: React.FC<RouteComponentProps<any>> = () => {
  const qs = parseQs(location.search.substr(1));
  const params: ShoppingCartQueryParams = asSortParams(
    qs,
    ShoppingCartSortField,
    ShoppingCartSortField.solde_to
  );
  return <ShoppingCartExport params={params} />;
};

const CreateOrderView: React.FC<RouteComponentProps<any>> = ({
  match,
  location
}) => {
  const qs = parseQs(location.search.substr(1));

  // Check user's permission
  if (isStaffRole()) {
    // Staff
    return <CreateOrder id={decodeURIComponent(match.params.id)} />;
  }
  // Customer
  return (
    <CustomerCreateOrder id={decodeURIComponent(match.params.id)} params={qs} />
  );
};

const CreateOrderExportView: React.FC<RouteComponentProps<any>> = ({
  match,
  location
}) => {
  const qs = parseQs(location.search.substr(1));
  const params: ExportOrderQueryParams = asSortParams(
    qs,
    ExportOrderSortField,
    ExportOrderSortField.item_no
  );
  return (
    <CreateOrderExport
      id={decodeURIComponent(match.params.id)}
      params={params}
    />
  );
};

const CreateOrderDomesticView: React.FC<RouteComponentProps<any>> = ({
  match,
  location
}) => {
  const qs = parseQs(location.search.substr(1));
  const params: DomesticOrderQueryParams = asSortParams(
    qs,
    DomesticOrderSortField,
    DomesticOrderSortField.item_no
  );
  return (
    <CreateOrderDomestic
      id={decodeURIComponent(match.params.id)}
      params={params}
    />
  );
};

const CreateOrderSection = () => {
  const intl = useIntl();
  const { zone } = useUser();
  const permissions = useUserPermissions();

  const getOrderComponents = () => {
    if (zone === Zone.Export) {
      return ExportSoldToList;
    } else {
      return isStaffRole() ? SoldToList : ContractListView;
    }
  };
  const getShoppingCartView = () => {
    const extensionsPermissionExport = permissions?.find(
      perm => perm.code === PermissionEnum.VIEW_ORDER_EXPORT
    );
    const extensionsPermissionDomestic = permissions?.find(
      perm => perm.code === PermissionEnum.VIEW_ORDER_DOMESTIC
    );
    if (
      (extensionsPermissionExport && !extensionsPermissionDomestic) ||
      zone === Zone.Export
    ) {
      return ShoppingCartExportView;
    } else {
      return Cart;
    }
  };
  const getCreateOrderView = () => {
    const extensionsPermissionExport = permissions?.find(
      perm => perm.code === PermissionEnum.VIEW_ORDER_EXPORT
    );
    const extensionsPermissionDomestic = permissions?.find(
      perm => perm.code === PermissionEnum.VIEW_ORDER_DOMESTIC
    );
    if (
      (extensionsPermissionExport && !extensionsPermissionDomestic) ||
      zone === Zone.Export
    ) {
      return CreateOrderExportView;
    } else if (extensionsPermissionDomestic) {
      return CreateOrderDomesticView;
    } else {
      return CreateOrderView;
    }
  };
  const getShoppingCartDetailView = () => {
    const extensionsPermissionExport = permissions?.find(
      perm => perm.code === PermissionEnum.VIEW_ORDER_EXPORT
    );
    const extensionsPermissionDomestic = permissions?.find(
      perm => perm.code === PermissionEnum.VIEW_ORDER_DOMESTIC
    );
    if (
      (extensionsPermissionExport && !extensionsPermissionDomestic) ||
      zone === Zone.Export
    ) {
      return CartDetailtExportListView;
    } else {
      return CartDetailtListView;
    }
  };

  return (
    <>
      <WindowTitle title={intl.formatMessage(sectionNames.createOrder)} />
      <Switch>
        <Route exact path={createOrderUrl} component={getOrderComponents()} />
        <Route exact path={shoppingCart} component={getShoppingCartView()} />
        <Route exact path={orderProcessing} component={OrderProcessing} />
        <Route
          exact
          path={orderCustomerProcessingFailed}
          component={OrderProcessingFailedView}
        />
        <Route
          exact
          path={orderCartPath(":id")}
          component={getCreateOrderView()}
        />
        <Route
          exact
          path={previewOrderPath(":id")}
          component={PreviewOrderView}
        />
        <Route exact path={contractPath(":id")} component={ContractListView} />
        <Route
          exact
          path={contractDetailPath(":id")}
          component={ContracDetailtListView}
        />
        <Route
          exact
          path={orderPath(":id")}
          component={getShoppingCartDetailView()}
        />
      </Switch>
    </>
  );
};

export default CreateOrderSection;
